// Small in-memory grid thumbnails for the editor's photo grids.
//
// The problem: the tray / slot-chooser / mobile "Mes photos" sheet render EVERY
// album photo at the 1600px `signed_url` tier. A decoded bitmap costs
// width*height*4 bytes of RAM regardless of file size — a 1600x1200 thumbnail is
// ~7.7MB EACH — so a 100-photo grid holds ~770MB and iOS reloads (discards) the
// page. CSS content-visibility doesn't reliably free these on Safari.
//
// The fix: downscale each grid image to ~320px (~0.4MB decoded, ~25x less) OFF
// the main thread (reusing the upload worker pool), cache the small blob URL, and
// point the grid <img> at it. The full 1600px is only ever decoded transiently
// inside the worker (bounded concurrency) and released — never held resident on
// the main thread. No backend change, works for existing albums.

import { useEffect, useState } from 'react';
import { processForUpload } from './imagePipeline';

const GRID_DIM = 320;     // tiles are ~80px CSS × up to 3x DPR ≈ 240px → 320 is sharp
const GRID_QUALITY = 0.72;
const CACHE_MAX = 500;

const cache = new Map<string, string>();          // source URL → small object URL
const pending = new Map<string, Promise<string | null>>();

// Bound concurrency so we never hold more than a couple of transient full-size
// decodes at once (the whole point is to stay under the memory ceiling).
let active = 0;
const waiters: (() => void)[] = [];
function acquire(): Promise<void> {
  if (active < 2) { active++; return Promise.resolve(); }
  return new Promise((r) => waiters.push(() => { active++; r(); }));
}
function release(): void {
  active--;
  const next = waiters.shift();
  if (next) next();
}

/** Main-thread downscale fallback when the worker pipeline is unavailable. */
async function downscaleMainThread(blob: Blob): Promise<Blob | null> {
  try {
    const bmp = await createImageBitmap(blob, { resizeWidth: GRID_DIM, resizeQuality: 'medium' } as ImageBitmapOptions).catch(async () => {
      // resizeWidth unsupported → decode then scale.
      const full = await createImageBitmap(blob);
      return full;
    });
    const scale = Math.min(1, GRID_DIM / Math.max(bmp.width, bmp.height));
    const w = Math.max(1, Math.round(bmp.width * scale));
    const h = Math.max(1, Math.round(bmp.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) { bmp.close?.(); return null; }
    ctx.drawImage(bmp, 0, 0, w, h);
    bmp.close?.();
    const out: Blob | null = await new Promise((res) => canvas.toBlob((b) => res(b), 'image/jpeg', GRID_QUALITY));
    canvas.width = 0; canvas.height = 0;
    return out;
  } catch {
    return null;
  }
}

/** Returns a cached ~320px object URL for the given source URL, generating it
 *  off-thread on first request. Returns null only if both worker + main-thread
 *  downscale fail (caller falls back to the original URL). */
export async function getGridThumb(srcUrl: string): Promise<string | null> {
  if (!srcUrl) return null;
  const cached = cache.get(srcUrl);
  if (cached) return cached;
  const inFlight = pending.get(srcUrl);
  if (inFlight) return inFlight;

  const job = (async (): Promise<string | null> => {
    await acquire();
    try {
      const resp = await fetch(srcUrl);
      if (!resp.ok) return null;
      const blob = await resp.blob();
      const small = (await processForUpload(blob, GRID_DIM, GRID_QUALITY)) ?? (await downscaleMainThread(blob));
      if (!small) return null;
      const url = URL.createObjectURL(small);
      // LRU bound — revoke the oldest blob URL so the cache itself can't leak.
      if (cache.size >= CACHE_MAX) {
        const oldest = cache.keys().next().value;
        if (oldest !== undefined) {
          const u = cache.get(oldest);
          if (u) { try { URL.revokeObjectURL(u); } catch { /* ignore */ } }
          cache.delete(oldest);
        }
      }
      cache.set(srcUrl, url);
      return url;
    } catch {
      return null;
    } finally {
      release();
      pending.delete(srcUrl);
    }
  })();

  pending.set(srcUrl, job);
  return job;
}

/** Revoke + clear all cached grid thumbnails (call on editor unmount). */
export function clearGridThumbs(): void {
  for (const u of cache.values()) { if (u) { try { URL.revokeObjectURL(u); } catch { /* ignore */ } } }
  cache.clear();
  pending.clear();
}

/** Hook: given a list of photos, returns a Map<id, smallUrl> that fills in as
 *  each thumbnail is generated. Render `map.get(p.id) ?? placeholder` so the
 *  full-size image is NEVER decoded in the grid. getGridThumb is cached, so
 *  re-runs are cheap (cache hits). */
export function useGridThumbs(
  photos: Array<{ id: string; signed_url: string | null }>,
): Map<string, string> {
  const [map, setMap] = useState<Map<string, string>>(new Map());
  // Stable dependency: only re-run when the set of (id→url) actually changes.
  const key = photos.map((p) => `${p.id}:${p.signed_url ?? ''}`).join('|');
  useEffect(() => {
    let alive = true;
    for (const p of photos) {
      if (!p.signed_url) continue;
      const src = p.signed_url;
      void getGridThumb(src).then((u) => {
        if (!alive) return;
        // Fall back to the original on the rare double-failure (worker AND
        // main-thread downscale) so a tile is never permanently blank.
        const val = u ?? src;
        setMap((prev) => {
          if (prev.get(p.id) === val) return prev;
          const next = new Map(prev);
          next.set(p.id, val);
          return next;
        });
      });
    }
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return map;
}
